import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MENU_CONFIG } from "../data/menu/MenuModule";
import { checkVisibility } from "../logic/Menu/filterMenuPermissions";

// Types
export interface Workspace {
  id: string;
  name: string;
  type: "website";
  icon?: string;
  metadata: {
    site_name: string;
    site_url: string;
    ui_site_name: string;
    dimensions: Array<{
      dimension_shared_uid: string;
      dimension_name: string;
    }>;
    [key: string]: any;
  };
}

export interface MenuItem {
  id: string;
  label: string;
  path: string;
  icon?: string;
  children?: MenuItem[];
  requiredPermissions?: string[];
  deviceSupport: ("desktop" | "mobile")[];
}

export interface Permission {
  resource: string;
  actions: ("read" | "write" | "delete")[];
}

export interface FeatureFlags {
  [key: string]: boolean;
}

interface WorkspaceState {
  selected: Workspace | null;
  available: Workspace[];
  allApiWebsites: Workspace[];
  isLoading: boolean;
  isLoadingAllWebsites: boolean;
  error: string | null;
  menu: MenuItem[];
  permissions: Permission[];
  features: FeatureFlags;
  metadata: Record<string, any>;
  // Set by single-site-locked modules (e.g. Optifund Dashboard - see
  // docs/new-module-checklist.md Section 10.6) via useLockWorkspaceToSite
  // while the user is on that screen, so WorkspaceDropdown can disable
  // switching instead of offering sites the screen doesn't support.
  locked: boolean;
}

const initialState: WorkspaceState = {
  selected: null,
  available: [],
  allApiWebsites: [],
  isLoading: false,
  isLoadingAllWebsites: false,
  error: null,
  menu: [],
  permissions: [],
  features: {},
  metadata: {},
  locked: false,
};

// Transformation utilities
export const transformWebsitePermissionsToWorkspaces = (
  websitePermissions: any[]
): Workspace[] => {
  if (!websitePermissions || !Array.isArray(websitePermissions)) {
    return [];
  }

  return websitePermissions.map((item) => {
    const [uuid, data] = Object.entries(item)[0] as [string, any];

    return {
      id: uuid,
      name: data.ui_site_name || data.site_name,
      type: "website" as const,
      icon: data.site_name?.[0]?.toUpperCase() || "🌐",
      metadata: {
        site_name: data.site_name,
        site_url: data.site_url,
        ui_site_name: data.ui_site_name,
        dimensions: data.dimensions || [],
      },
    };
  });
};

export const transformProductsToPermissions = (products: any): Permission[] => {
  if (!products) return [];

  const permissions: Permission[] = [];
  const resourceMap = new Map<string, Set<"read" | "write" | "delete">>();

  Object.entries(products).forEach(([productKey, productValue]) => {
    if (Array.isArray(productValue)) {
      productValue.forEach((item: any) => {
        Object.entries(item).forEach(([featureKey, featureValue]) => {
          if (Array.isArray(featureValue)) {
            featureValue.forEach((subItem: any) => {
              Object.entries(subItem).forEach(([subFeatureKey, endpoints]) => {
                if (Array.isArray(endpoints)) {
                  const methods = new Set<string>();
                  endpoints.forEach((endpoint: any) => {
                    if (endpoint.method) {
                      methods.add(endpoint.method);
                    }
                  });

                  const actions: ("read" | "write" | "delete")[] = [];
                  if (methods.has("GET")) actions.push("read");
                  if (
                    methods.has("POST") ||
                    methods.has("PUT") ||
                    methods.has("PATCH")
                  ) {
                    actions.push("write");
                  }
                  if (methods.has("DELETE")) actions.push("delete");

                  const resource = `${productKey}.${featureKey}.${subFeatureKey}`;

                  // Store in map for hierarchical permissions
                  const parts = resource.split(".");
                  for (let i = 1; i <= parts.length; i++) {
                    const partialResource = parts.slice(0, i).join(".");
                    if (!resourceMap.has(partialResource)) {
                      resourceMap.set(partialResource, new Set());
                    }
                    actions.forEach((action) => {
                      resourceMap.get(partialResource)!.add(action);
                    });
                  }
                }
              });
            });
          }
        });
      });
    }
  });

  resourceMap.forEach((actions, resource) => {
    permissions.push({
      resource,
      actions: Array.from(actions),
    });
  });

  return permissions;
};

export const buildMenuFromPermissions = (
  _: Permission[],
  userData: any
): MenuItem[] => {
  // Use your existing menu filtering logic
  const userDataPermissions = userData?.products;
  const visibleMenu = checkVisibility(MENU_CONFIG, userDataPermissions);

  // Convert your existing menu format to MenuItem format
  const convertToMenuItem = (item: any): MenuItem => {
    return {
      id: item.label || item.id,
      label: item.label,
      path: item.path || "#",
      icon: item.icon,
      children: item.children?.map((child: any) => convertToMenuItem(child)),
      requiredPermissions: item.visibility || [],
      deviceSupport: ["desktop", "mobile"], // All menu items support both for now
    };
  };

  return visibleMenu.map((item: any) => convertToMenuItem(item));
};

// Slice
const workspaceSlice = createSlice({
  name: "workspace",
  initialState,
  reducers: {
    initializeWorkspaces: (state, action: PayloadAction<any>) => {
      const userData = action.payload;
      state.available = transformWebsitePermissionsToWorkspaces(
        userData.website_permissions
      );

      if (state.available.length === 1 && !state.selected) {
        state.selected = state.available[0];
      }

      state.isLoading = false;
      state.error = null;
    },

    setSelectedWorkspace: (state, action: PayloadAction<Workspace>) => {
      state.selected = action.payload;
    },

    setAllApiWebsites: (state, action: PayloadAction<Workspace[]>) => {
      state.allApiWebsites = action.payload;
    },

    setIsLoadingAllWebsites: (state, action: PayloadAction<boolean>) => {
      state.isLoadingAllWebsites = action.payload;
    },

    initializeWorkspaceData: (state, action: PayloadAction<any>) => {
      const userData = action.payload;
      state.permissions = transformProductsToPermissions(userData.products);

      // Pass full userData to buildMenuFromPermissions
      state.menu = buildMenuFromPermissions(state.permissions, userData);

      state.features = {
        newDashboard: true,
        betaFeatures: false,
      };
      state.isLoading = false;
    },

    // FIXED: Properly clear workspace state
    clearSelectedWorkspace: (state) => {
      state.selected = null;
      state.menu = [];
      state.permissions = [];
      state.features = {};
      state.metadata = {};
      state.available = [];
      state.allApiWebsites = [];
      state.isLoadingAllWebsites = false;
      state.locked = false;
    },

    resetWorkspaceState: (state) => {
      state.selected = null;
      state.available = [];
      state.allApiWebsites = [];
      state.isLoading = false;
      state.isLoadingAllWebsites = false;
      state.menu = [];
      state.permissions = [];
      state.features = {};
      state.metadata = {};
      state.error = null;
      state.locked = false;
    },

    setWorkspaceLocked: (state, action: PayloadAction<boolean>) => {
      state.locked = action.payload;
    },

    setWorkspaceMenu: (state, action: PayloadAction<MenuItem[]>) => {
      state.menu = action.payload;
    },

    setWorkspacePermissions: (state, action: PayloadAction<Permission[]>) => {
      state.permissions = action.payload;
    },

    clearWorkspaceMenu: (state) => {
      state.menu = [];
    },

    clearWorkspacePermissions: (state) => {
      state.permissions = [];
    },
  },
});

export const {
  initializeWorkspaces,
  setSelectedWorkspace,
  setAllApiWebsites,
  setIsLoadingAllWebsites,
  initializeWorkspaceData,
  clearSelectedWorkspace,
  resetWorkspaceState,
  setWorkspaceMenu,
  setWorkspacePermissions,
  clearWorkspaceMenu,
  clearWorkspacePermissions,
  setWorkspaceLocked,
} = workspaceSlice.actions;

export const selectSelectedWorkspace = (state: any) => state.workspace.selected;

export const selectAvailableWorkspaces = (state: any) =>
  state.workspace.available;

export const selectAllApiWebsites = (state: any) =>
  state.workspace.allApiWebsites;

export const selectIsLoadingAllWebsites = (state: any) =>
  state.workspace.isLoadingAllWebsites;

export const selectWorkspaceMenu = (state: any) => state.workspace.menu;

export const selectWorkspacePermissions = (state: any) =>
  state.workspace.permissions;

export const selectWorkspaceFeatures = (state: any) => state.workspace.features;

export const selectWorkspaceLoading = (state: any) => state.workspace.isLoading;

export const selectWorkspaceError = (state: any) => state.workspace.error;

export const selectIsWorkspaceLocked = (state: any) => state.workspace.locked;

export default workspaceSlice.reducer;
